import { useState } from "react";
import {
  MagnifyingGlass,
  PaperPlaneTilt,
  Password,
  Prohibit,
  ShieldCheck,
  SignOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "../lib/api";


const EMPTY_PASSWORD = {
  admin_current_password: "",
  new_password: "",
  confirm_new_password: "",
};


export default function ConsumerUserManagement() {
  const [searchType, setSearchType] = useState("email");
  const [query, setQuery] = useState("");
  const [record, setRecord] = useState(null);
  const [audit, setAudit] = useState([]);
  const [password, setPassword] = useState(EMPTY_PASSWORD);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const loadAudit = async (userId) => {
    const { data } = await api.get(`/admin/consumer-users/${userId}/audit`);
    setAudit(data);
  };

  const search = async (event) => {
    event.preventDefault();
    setBusy("search");
    setError("");
    try {
      const { data } = await api.post("/admin/consumer-users/search", {
        [searchType]: query.trim(),
      });
      setRecord(data);
      await loadAudit(data.user.user_id);
    } catch (requestError) {
      setRecord(null);
      setAudit([]);
      setError(requestError.response?.data?.detail || "Consumer account not found.");
    } finally {
      setBusy("");
    }
  };

  const runAction = async (action, request, successMessage) => {
    setBusy(action);
    setError("");
    try {
      await request();
      toast.success(successMessage);
      const { data } = await api.post("/admin/consumer-users/search", {
        user_id: record.user.user_id,
      });
      setRecord(data);
      await loadAudit(record.user.user_id);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Account action failed.");
    } finally {
      setBusy("");
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    if (password.new_password !== password.confirm_new_password) {
      setError("Password confirmation does not match.");
      return;
    }
    await runAction(
      "password",
      () => api.post(
        `/admin/consumer-users/${record.user.user_id}/password`,
        password,
      ),
      "Consumer password reset and sessions revoked.",
    );
    setPassword(EMPTY_PASSWORD);
  };

  return (
    <div className="space-y-6" data-testid="consumer-user-management">
      <div>
        <div className="overline text-[var(--cc-gold)] mb-1">Consumer accounts</div>
        <h2 className="font-display text-2xl font-semibold">User management</h2>
      </div>

      <form onSubmit={search} className="flex flex-col md:flex-row gap-3" data-testid="consumer-user-search">
        <label className="block md:w-48">
          <span className="overline text-white/50 block mb-1.5">Search by</span>
          <select
            value={searchType}
            onChange={(event) => setSearchType(event.target.value)}
            className="w-full bg-[var(--cc-navy)] border border-white/15 rounded px-3 py-2 text-sm"
          >
            <option value="email">Exact email</option>
            <option value="user_id">Exact user ID</option>
          </select>
        </label>
        <label className="block flex-1">
          <span className="overline text-white/50 block mb-1.5">
            {searchType === "email" ? "Consumer email" : "Consumer user ID"}
          </span>
          <input
            type={searchType === "email" ? "email" : "text"}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            required
            className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
          />
        </label>
        <button type="submit" disabled={busy === "search"} className="btn-gold self-end px-4 py-2 rounded text-sm inline-flex items-center gap-2">
          <MagnifyingGlass size={16} /> {busy === "search" ? "Searching..." : "Search"}
        </button>
      </form>

      {error && <div role="alert" className="text-red-300 text-sm" data-testid="consumer-admin-error">{error}</div>}

      {record && (
        <>
          <section className="border border-white/10 rounded-lg p-5" data-testid="consumer-admin-result">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm flex-1">
                <DataRow label="Email" value={record.user.email} />
                <DataRow label="User ID" value={record.user.user_id} mono />
                <DataRow label="Status" value={record.user.status} />
                <DataRow label="Email verified" value={record.user.email_verified ? "Yes" : "No"} />
                <DataRow label="Subscription" value={record.subscription.status} />
                <DataRow label="Active sessions" value={record.active_session_count} />
              </dl>
              <div className="flex flex-wrap gap-2">
                <ActionButton
                  icon={SignOut}
                  label="Revoke sessions"
                  disabled={Boolean(busy)}
                  onClick={() => runAction(
                    "sessions",
                    () => api.post(`/admin/consumer-users/${record.user.user_id}/revoke-sessions`),
                    "Consumer sessions revoked.",
                  )}
                />
                <ActionButton
                  icon={PaperPlaneTilt}
                  label="Send reset email"
                  disabled={Boolean(busy)}
                  onClick={() => runAction(
                    "email",
                    () => api.post(`/admin/consumer-users/${record.user.user_id}/password-reset-email`),
                    "Password reset email requested.",
                  )}
                />
                <ActionButton
                  icon={record.user.status === "SUSPENDED" ? ShieldCheck : Prohibit}
                  label={record.user.status === "SUSPENDED" ? "Reactivate" : "Suspend"}
                  disabled={Boolean(busy)}
                  onClick={() => runAction(
                    "status",
                    () => api.post(
                      `/admin/consumer-users/${record.user.user_id}/status`,
                      null,
                      { params: { status: record.user.status === "SUSPENDED" ? "active" : "suspended" } },
                    ),
                    record.user.status === "SUSPENDED" ? "Consumer reactivated." : "Consumer suspended.",
                  )}
                />
              </div>
            </div>

            <div className="mt-5 border-t border-white/10 pt-4">
              <div className="overline text-white/50 mb-3">Privacy choices</div>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                {Object.entries(record.privacy).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between gap-4">
                    <span>{key.replaceAll("_", " ")}</span>
                    <span className={value ? "text-emerald-300" : "text-white/50"}>{value ? "On" : "Off"}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <form onSubmit={resetPassword} className="border border-white/10 rounded-lg p-5 space-y-3" data-testid="consumer-admin-password-form">
            <div className="overline text-[var(--cc-gold)] inline-flex items-center gap-2"><Password size={15} /> Direct password reset</div>
            <p className="text-sm text-white/60">This revokes every consumer session. The existing password is never shown.</p>
            <PasswordInput label="Your admin password" value={password.admin_current_password} onChange={(value) => setPassword({ ...password, admin_current_password: value })} />
            <PasswordInput label="New consumer password" value={password.new_password} onChange={(value) => setPassword({ ...password, new_password: value })} />
            <PasswordInput label="Confirm new consumer password" value={password.confirm_new_password} onChange={(value) => setPassword({ ...password, confirm_new_password: value })} />
            <button type="submit" disabled={Boolean(busy)} className="btn-gold px-4 py-2 rounded text-sm">Reset password</button>
          </form>

          <section className="border border-white/10 rounded-lg overflow-hidden">
            <div className="px-4 py-3 overline text-white/50">Admin action history</div>
            <div className="divide-y divide-white/10" data-testid="consumer-admin-audit">
              {audit.map((event) => (
                <div key={event.event_id} className="px-4 py-3 text-sm flex flex-wrap justify-between gap-2">
                  <span>{event.action}</span>
                  <span className="text-white/50">{new Date(event.created_at).toLocaleString("en-GB")}</span>
                </div>
              ))}
              {audit.length === 0 && <div className="px-4 py-5 text-sm text-white/50">No admin actions recorded.</div>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}


const DataRow = ({ label, value, mono = false }) => (
  <div className="contents">
    <dt className="text-white/50">{label}</dt>
    <dd className={mono ? "font-mono-num break-all" : ""}>{String(value ?? "-")}</dd>
  </div>
);


const ActionButton = ({ icon: Icon, label, ...props }) => (
  <button type="button" className="border border-white/15 rounded px-3 py-2 text-sm inline-flex items-center gap-2 hover:border-white/30 disabled:opacity-50" {...props}>
    <Icon size={15} /> {label}
  </button>
);


const PasswordInput = ({ label, value, onChange }) => (
  <label className="block">
    <span className="overline text-white/50 block mb-1.5">{label}</span>
    <input
      type="password"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      minLength={12}
      maxLength={1024}
      required
      autoComplete="new-password"
      className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
    />
  </label>
);
